import { AxiosResponse } from 'axios';
import { jobBoardAPI } from './base';
import {
  CreateTaskDTO,
  UpdateTaskDTO
} from 'data/from-data/create-task-form.data';
import { SubTask } from 'pages/apps/tasks/types/formFields';

export const taskEndPoint = {
  getAllTask: '/Task',
  create: '/task',
  update: '/task/:{id}',
  createTask: '/task/tasks',
  updateTask: '/task/tasks/:{id}',
  getTasks: '/task/tasks',
  getById: '/task/:{id}',
  duplicate: '/task/duplicate/:{id}',
  comments: '/task/tasks/:{id}/comments',
  filteredTasks: '/task/tasks',
  delete: '/task/tasks/:{id}',
  getActivityLogs: '/task/task-activity-logs',
  getActivityLogsByTaskId: '/task/task-activity-logs/task/:{id}',
  duplicateTask: '/task/tasks/duplicate/:{id}',
  markAsCompleted: '/task/tasks/:{id}/complete',
  getTaskById: '/task/tasks/:{id}',
  updateTaskStatus: '/task/tasks/:{id}/status'
};

export interface TaskResponse {
  _id: string;
  id?: string;
  task: string;
  description?: string;
  date: string;
  time: string;
  completed: boolean;
  status: string;
  priority: string;
  assignedTo: {
    _id?: string;
    id?: string;
    email: string;
    firstName: string;
    lastName: string;
    username: string;
    phoneNumber: string;
  }[];
  subTask: SubTask[];
  creator: {
    _id?: string;
    id?: string;
    firstName: string;
    lastName: string;
    email: string;
    username: string;
  };
  jobId: string | null;
  job?: {
    id?: string;
    _id?: string;
    isPriority: boolean;
    title: string;
    well?: string;
    jobNumber: string;
    customer?: { id?: string; organizationName: string };
  };
  createdAt: string;
  updatedAt: string;
  __v?: number;
  comments?: {
    text: string;
    userId: string;
    createdAt: string;
    editedAt?: string;
  }[];
  stageReminder?: {
    triggerType: 'any_stage_change' | 'specific_stages';
    stageIds: string[];
  } | null;
}

export const GET_TASKS_QUERY = async (): Promise<
  AxiosResponse<TaskResponse[]>
> => {
  const { data } = await jobBoardAPI.get(taskEndPoint.getAllTask);
  return data;
};

export const CREATE_TASK = async (
  data: CreateTaskDTO
): Promise<AxiosResponse<TaskResponse>> =>
  jobBoardAPI.post(taskEndPoint.create, data);

export const DELETE_TASK = async (
  taskId: string
): Promise<AxiosResponse<void>> => {
  const endpoint = taskEndPoint.delete.replace(':{id}', taskId);
  return jobBoardAPI.delete(endpoint);
};

export const UPDATE_TASK = async ({
  taskId,
  data
}: {
  taskId: string;
  data: UpdateTaskDTO | any;
}): Promise<AxiosResponse<TaskResponse>> =>
  jobBoardAPI.patch(taskEndPoint.update.replace(':{id}', taskId), data);

export const ASSIGN_TASK = async ({
  taskId,
  employeeIds
}: {
  taskId: string;
  employeeIds: Array<string>;
}): Promise<AxiosResponse<TaskResponse>> => {
  return jobBoardAPI.post(taskEndPoint.update.replace(':{id}', taskId), {
    taskId,
    employeeIds
  });
};

export const SET_DESCRIPTION = async ({
  formData,
  taskId
}: {
  formData: { description: string };
  taskId: string;
}): Promise<any> => {
  return jobBoardAPI.patch(
    taskEndPoint.update.replace(':{id}', taskId),
    formData
  );
};

export const GET_ALL_TASKS = async (): Promise<
  AxiosResponse<TaskResponse[]>
> => {
  return jobBoardAPI.get(taskEndPoint.getTasks);
};

export const GET_TASK_BY_ID = async (
  taskId: string
): Promise<AxiosResponse<TaskResponse>> => {
  return jobBoardAPI.get(taskEndPoint.getTaskById.replace(':{id}', taskId));
};

export const DUPLICATE_TASK = async (
  taskId: string
): Promise<AxiosResponse<TaskResponse>> => {
  return jobBoardAPI.post(taskEndPoint.duplicateTask.replace(':{id}', taskId));
};

export const ADD_COMMENT = async ({
  taskId,
  text,
  userId
}: {
  taskId: string;
  text: string;
  userId: string;
}): Promise<AxiosResponse<any>> => {
  return jobBoardAPI.post(taskEndPoint.comments.replace(':{id}', taskId), {
    text,
    userId
  });
};

export const GET_FILTERED_TASKS = async (filters?: {
  assignedTo?: string;
  creator?: string;
  priority?: string;
  status?: string;
  dueDate?: string;
  sortBy?: string;
  sortOrder?: string;
  jobId?: string;
}): Promise<AxiosResponse<TaskResponse[]>> => {
  return jobBoardAPI.get(taskEndPoint.filteredTasks, { params: filters });
};

export const CREATE_TASKS = async (
  data: CreateTaskDTO
): Promise<AxiosResponse<TaskResponse>> =>
  jobBoardAPI.post(taskEndPoint.createTask, data);

export const UPDATE_TASKS = async ({
  taskId,
  data
}: {
  taskId: string;
  data: UpdateTaskDTO;
}): Promise<AxiosResponse<TaskResponse>> =>
  jobBoardAPI.patch(taskEndPoint.updateTask.replace(':{id}', taskId), data);

export const GET_ACTIVITY_LOGS = async (): Promise<AxiosResponse<any>> => {
  return jobBoardAPI.get(taskEndPoint.getActivityLogs);
};

export const GET_ACTIVITY_LOGS_BY_TASK_ID = async (
  taskId: string
): Promise<AxiosResponse<any>> => {
  return jobBoardAPI.get(
    taskEndPoint.getActivityLogsByTaskId.replace(':{id}', taskId)
  );
};

export const MARK_AS_COMPLETED = async (
  taskId: string
): Promise<AxiosResponse<TaskResponse>> => {
  return jobBoardAPI.patch(
    taskEndPoint.markAsCompleted.replace(':{id}', taskId)
  );
};

export const UPDATE_TASK_STATUS = async ({
  taskId,
  status
}: {
  taskId: string;
  status: string;
}): Promise<AxiosResponse<TaskResponse>> => {
  return jobBoardAPI.patch(
    taskEndPoint.updateTaskStatus.replace(':{id}', taskId),
    { status }
  );
};

export const UPDATE_SUBTASK_STATUS = async ({
  taskId,
  subTaskIndex,
  completed,
  completedAt,
  completedBy
}: {
  taskId: string;
  subTaskIndex: number;
  completed: boolean;
  completedAt: string | null;
  completedBy: string | null;
}) => {
  return jobBoardAPI.patch(`/task/tasks/${taskId}/subtask/${subTaskIndex}`, {
    completed,
    completedAt,
    completedBy
  });
};
